/**
 * useQueue - React hook for real-time queue management
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  QueueTicket,
  takeTicket as takeTicketFn,
  advanceQueue as advanceQueueFn,
  markArrival as markArrivalFn,
  startService as startServiceFn,
  completeTicket as completeTicketFn,
  cancelTicket as cancelTicketFn,
  getUserActiveTicket,
  subscribeToUserActiveTickets,
  subscribeToQueueByBranch,
  subscribeToQueueByBarber,
  subscribeToTicket,
  getTimeRemaining,
  formatTimeRemaining,
  TakeTicketRequest,
  AdvanceQueueRequest,
  MarkArrivalRequest,
  StartServiceRequest,
  CompleteTicketRequest,
  CancelTicketRequest
} from '../services/queueService';

interface UseQueueOptions {
  branchId?: string;
  barberId?: string; // Para filtrar cola del barbero
  queueId?: string;
}

interface UseQueueReturn {
  // Current user's ticket
  myTicket: QueueTicket | null;
  myTicketLoading: boolean;

  // Turnos de los acompañantes que vinieron con este cliente (mismo userId,
  // `isCompanion: true`). Vacío si vino solo.
  myCompanionTickets: QueueTicket[];

  // All tickets in branch queue
  queueTickets: QueueTicket[];
  queueLoading: boolean;

  // Specific ticket (for tracking)
  ticket: QueueTicket | null;
  ticketLoading: boolean;

  // Timer for current ticket
  timeRemaining: number;
  timeRemainingFormatted: string;

  // Actions
  takeTicket: (data: TakeTicketRequest) => Promise<void>;
  advanceQueue: (data: AdvanceQueueRequest) => Promise<void>;
  markArrival: (data: MarkArrivalRequest) => Promise<void>;
  startService: (data: StartServiceRequest) => Promise<void>;
  completeTicket: (data: CompleteTicketRequest) => Promise<void>;
  cancelTicket: (data: CancelTicketRequest) => Promise<void>;

  // Loading states
  taking: boolean;
  advancing: boolean;
  marking: boolean;
  starting: boolean;
  completing: boolean;
  cancelling: boolean;

  // Error state
  error: string | null;
}

/**
 * Hook for queue management with real-time updates
 */
export function useQueue(options: UseQueueOptions = {}): UseQueueReturn {
  const { branchId, barberId, queueId } = options;
  const { firebaseUser } = useAuth();

  // State
  const [myTicket, setMyTicket] = useState<QueueTicket | null>(null);
  const [myCompanionTickets, setMyCompanionTickets] = useState<QueueTicket[]>([]);
  const [myTicketLoading, setMyTicketLoading] = useState(false);

  const [queueTickets, setQueueTickets] = useState<QueueTicket[]>([]);
  const [queueLoading, setQueueLoading] = useState(false);

  const [ticket, setTicket] = useState<QueueTicket | null>(null);
  const [ticketLoading, setTicketLoading] = useState(false);

  const [timeRemaining, setTimeRemaining] = useState(0);

  const [taking, setTaking] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [marking, setMarking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Subscribe to user's active ticket at branch (real-time)
  useEffect(() => {
    if (!firebaseUser?.uid || !branchId) {
      setMyTicket(null);
      setMyCompanionTickets([]);
      setMyTicketLoading(false);
      return;
    }

    setMyTicketLoading(true);

    const unsubscribe = subscribeToUserActiveTickets(
      firebaseUser.uid,
      branchId,
      (tickets) => {
        // El propio y los de los acompañantes llegan juntos porque comparten
        // userId; se separan por `isCompanion` para no mezclarlos en la pantalla.
        setMyTicket(tickets.find((t) => t.isCompanion !== true) ?? null);
        setMyCompanionTickets(tickets.filter((t) => t.isCompanion === true));
        setMyTicketLoading(false);
      },
      (err) => {
        console.error('Error subscribing to user active tickets:', err);
        setError(err.message);
        setMyTicketLoading(false);
      }
    );

    return () => unsubscribe();
  }, [firebaseUser?.uid, branchId]);

  // Subscribe to queue (real-time) - by barber or by branch
  useEffect(() => {
    // Si hay barberId, filtrar por barbero
    if (barberId && branchId) {
      setQueueLoading(true);

      const unsubscribe = subscribeToQueueByBarber(
        barberId,
        branchId,
        (tickets) => {
          setQueueTickets(tickets);
          setQueueLoading(false);
        },
        (err) => {
          console.error('Error subscribing to barber queue:', err);
          setError(err.message);
          setQueueLoading(false);
        }
      );

      return () => unsubscribe();
    }

    // Si hay branchId (pero no barberId), filtrar por sucursal
    if (!branchId) {
      setQueueTickets([]);
      return;
    }

    setQueueLoading(true);

    const unsubscribe = subscribeToQueueByBranch(
      branchId,
      (tickets) => {
        setQueueTickets(tickets);
        setQueueLoading(false);
      },
      (err) => {
        console.error('Error subscribing to queue:', err);
        setError(err.message);
        setQueueLoading(false);
      }
    );

    return () => unsubscribe();
  }, [branchId, barberId]);

  // Subscribe to specific ticket (real-time)
  useEffect(() => {
    if (!queueId) {
      setTicket(null);
      return;
    }

    setTicketLoading(true);

    const unsubscribe = subscribeToTicket(
      queueId,
      (ticketData) => {
        setTicket(ticketData);
        setTicketLoading(false);
      },
      (err) => {
        console.error('Error subscribing to ticket:', err);
        setError(err.message);
        setTicketLoading(false);
      }
    );

    return () => unsubscribe();
  }, [queueId]);

  // Update timer every second
  useEffect(() => {
    const activeTicket = ticket || myTicket;

    if (!activeTicket?.timerExpiry) {
      setTimeRemaining(0);
      return;
    }

    // Initial calculation
    setTimeRemaining(getTimeRemaining(activeTicket.timerExpiry ?? null));

    // Update every second
    const interval = setInterval(() => {
      const remaining = getTimeRemaining(activeTicket.timerExpiry ?? null);
      setTimeRemaining(remaining);

      // Stop when timer expires
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [ticket, myTicket]);

  // Actions
  const takeTicket = useCallback(async (data: TakeTicketRequest) => {
    setTaking(true);
    setError(null);

    try {
      const result = await takeTicketFn(data);
      console.log('Ticket taken successfully:', result);

      // Refresh user's ticket
      if (firebaseUser?.uid && data.branchId) {
        const newTicket = await getUserActiveTicket(firebaseUser.uid, data.branchId);
        setMyTicket(newTicket);
      }
    } catch (err: any) {
      console.error('Error taking ticket:', err);
      setError(err.message || 'Failed to take ticket');
      throw err;
    } finally {
      setTaking(false);
    }
  }, [firebaseUser?.uid]);

  const advanceQueue = useCallback(async (data: AdvanceQueueRequest) => {
    setAdvancing(true);
    setError(null);

    try {
      const result = await advanceQueueFn(data);
      console.log('Queue advanced successfully:', result);
    } catch (err: any) {
      console.error('Error advancing queue:', err);
      setError(err.message || 'Failed to advance queue');
      throw err;
    } finally {
      setAdvancing(false);
    }
  }, []);

  const markArrival = useCallback(async (data: MarkArrivalRequest) => {
    setMarking(true);
    setError(null);

    try {
      const result = await markArrivalFn(data);
      console.log('Arrival marked successfully:', result);
    } catch (err: any) {
      console.error('Error marking arrival:', err);
      setError(err.message || 'Failed to mark arrival');
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  const startService = useCallback(async (data: StartServiceRequest) => {
    setStarting(true);
    setError(null);

    try {
      const result = await startServiceFn(data);
      console.log('Service started successfully:', result);
    } catch (err: any) {
      console.error('Error starting service:', err);
      setError(err.message || 'Failed to start service');
      throw err;
    } finally {
      setStarting(false);
    }
  }, []);

  const completeTicket = useCallback(async (data: CompleteTicketRequest) => {
    setCompleting(true);
    setError(null);

    try {
      const result = await completeTicketFn(data);
      console.log('Ticket completed successfully:', result);
    } catch (err: any) {
      console.error('Error completing ticket:', err);
      setError(err.message || 'Failed to complete ticket');
      throw err;
    } finally {
      setCompleting(false);
    }
  }, []);

  const cancelTicket = useCallback(async (data: CancelTicketRequest) => {
    setCancelling(true);
    setError(null);

    try {
      const result = await cancelTicketFn(data);
      console.log('Ticket cancelled successfully:', result);
    } catch (err: any) {
      console.error('Error cancelling ticket:', err);
      setError(err.message || 'Failed to cancel ticket');
      throw err;
    } finally {
      setCancelling(false);
    }
  }, []);

  return {
    // Current user's ticket
    myTicket,
    myTicketLoading,
    myCompanionTickets,

    // All tickets in branch queue
    queueTickets,
    queueLoading,

    // Specific ticket
    ticket,
    ticketLoading,

    // Timer
    timeRemaining,
    timeRemainingFormatted: formatTimeRemaining(timeRemaining),

    // Actions
    takeTicket,
    advanceQueue,
    markArrival,
    startService,
    completeTicket,
    cancelTicket,

    // Loading states
    taking,
    advancing,
    marking,
    starting,
    completing,
    cancelling,

    // Error
    error,
  };
}
